import { appHomeUrl } from '../utils/appUrl'

export type PrevioIdRedirectConfig = {
  /** Absolute url, or a path relative to the app origin when both apps share one origin (`/id`). */
  idAppUrl: string
  project: string
  /** Login route path including the router base (`router.resolve(...).href`). */
  loginPath: string
}

export enum PrevioIdEntryPoint {
  LOGIN = 'login',
  LOGOUT = 'logout',
}

const ENTRY_POINT_SUFFIX: Record<PrevioIdEntryPoint, string> = {
  [PrevioIdEntryPoint.LOGIN]: '',
  [PrevioIdEntryPoint.LOGOUT]: '/logout',
}

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(url)
  } catch {
    return null
  }
}

const withoutTrailingSlash = (path: string): string => {
  return path.replace(/\/+$/, '')
}

const isLoginUrl = (url: URL, loginPath: string): boolean => {
  return withoutTrailingSlash(url.pathname) === withoutTrailingSlash(loginPath)
}

const entryPointPath = (project: string, entryPoint: PrevioIdEntryPoint): string => {
  return `/entry-point/${project}${ENTRY_POINT_SUFFIX[entryPoint]}`
}

const sanitizeReturnUrl = (
  returnUrl: string | null,
  loginPath: string,
  appUrl: string,
): string | null => {
  if (!returnUrl) {
    return null
  }

  const url = parseUrl(returnUrl)

  if (url === null) {
    return appUrl
  }

  return isLoginUrl(url, loginPath) ? appUrl : returnUrl
}

/**
 * @param appUrl home url of the calling app (origin + router base); resolves a relative `idAppUrl`
 *   and is the returnUrl fallback when the current page is the login page itself
 */
export const buildPrevioIdEntryPointUrl = (
  config: PrevioIdRedirectConfig,
  entryPoint: PrevioIdEntryPoint,
  returnUrl: string | null,
  appUrl: string,
): string => {
  // string concatenation on purpose: `new URL('/entry-point/x', 'http://host/id')` would drop `/id`
  const entryPointUrl = new URL(
    withoutTrailingSlash(config.idAppUrl) + entryPointPath(config.project, entryPoint),
    appUrl,
  )
  const safeReturnUrl = sanitizeReturnUrl(returnUrl, config.loginPath, appUrl)

  if (safeReturnUrl !== null) {
    entryPointUrl.searchParams.set('returnUrl', safeReturnUrl)
  }

  return entryPointUrl.toString()
}

export class PrevioIdRedirect {
  constructor(private readonly resolveConfig: () => PrevioIdRedirectConfig) {}

  toLogin = (returnUrl: string | null): void => {
    this.replace(PrevioIdEntryPoint.LOGIN, returnUrl)
  }

  toLogout = (returnUrl: string | null): void => {
    this.replace(PrevioIdEntryPoint.LOGOUT, returnUrl)
  }

  private replace = (entryPoint: PrevioIdEntryPoint, returnUrl: string | null): void => {
    window.location.replace(
      buildPrevioIdEntryPointUrl(this.resolveConfig(), entryPoint, returnUrl, appHomeUrl()),
    )
  }
}
